import { newHonorariumClientRowId, type U3130FindProjectItem, type U3130HonorariumItem } from "../U3130TimesheetModel";

const TEMPLATE_VERSION = 1 as const;

/** Une ligne de modèle : structure projet / sous-projet / tâche sans heures de semaine. */
export type U3130HonorariumTemplateRow = {
  projectId: string;
  subprojectId: string;
  taskType: string;
  project?: U3130HonorariumItem["project"] | U3130FindProjectItem;
  subproject?: U3130HonorariumItem["subproject"];
  task?: U3130HonorariumItem["task"];
};

export type U3130HonorariumTemplatePayload = {
  version: typeof TEMPLATE_VERSION;
  rows: U3130HonorariumTemplateRow[];
};

export function getHonorariumTemplateStorageKey(userName: string, organizationId: string): string {
  return `U3130_${userName}_${organizationId || "noorg"}_honorariumTemplate`;
}

function resolveProjectId(row: U3130HonorariumItem): string {
  const pid = row.projectId?.trim();
  if (pid) return pid;
  const p = row.project;
  if (p && typeof p === "object" && "id" in p) {
    return String((p as { id?: string }).id ?? "").trim();
  }
  return "";
}

function resolveSubprojectId(row: U3130HonorariumItem): string {
  const sid = row.subprojectId?.trim();
  if (sid) return sid;
  const sp = row.subproject;
  if (sp && typeof sp === "object" && "id" in sp) {
    return String((sp as { id?: string }).id ?? "").trim();
  }
  return "";
}

function resolveTaskType(row: U3130HonorariumItem): string {
  return row.taskType?.trim() || row.task?.id?.trim() || "";
}

/** Extrait les lignes « complètes » (au moins un projet) pour persistance locale. */
export function serializeHonorariumTemplate(rows: U3130HonorariumItem[] | undefined | null): U3130HonorariumTemplateRow[] {
  const list = rows ?? [];
  return list.flatMap((row) => {
    const projectId = resolveProjectId(row);
    if (!projectId) return [];
    const item: U3130HonorariumTemplateRow = {
      projectId,
      subprojectId: resolveSubprojectId(row),
      taskType: resolveTaskType(row),
      project: row.project,
      subproject: row.subproject,
      task: row.task,
    };
    return [item];
  });
}

export function stringifyHonorariumTemplatePayload(rows: U3130HonorariumTemplateRow[]): string {
  const payload: U3130HonorariumTemplatePayload = { version: TEMPLATE_VERSION, rows };
  return JSON.stringify(payload);
}

export function parseHonorariumTemplatePayload(raw: string | null): U3130HonorariumTemplateRow[] | null {
  if (raw == null || raw.trim() === "") return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const obj = parsed as { version?: number; rows?: unknown };
    if (obj.version !== TEMPLATE_VERSION || !Array.isArray(obj.rows)) return null;
    return obj.rows as U3130HonorariumTemplateRow[];
  } catch {
    return null;
  }
}

/** Reconstruit des lignes formulaire (nouveaux `clientRowId`, heures à zéro). */
export function templateRowsToHonorariumItems(rows: U3130HonorariumTemplateRow[]): U3130HonorariumItem[] {
  return rows.map((r) => ({
    clientRowId: newHonorariumClientRowId(),
    projectId: r.projectId,
    subprojectId: r.subprojectId || "",
    taskType: r.taskType || "",
    project: r.project,
    subproject: r.subproject,
    task: r.task,
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
  }));
}
